import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Eye, EyeOff, Lock, Mail } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.getPublicLogo()
      .then((res) => {
        const value = res?.data?.logo;
        if (mounted && typeof value === 'string' && value.startsWith('data:image/')) {
          setLogo(value);
        }
      })
      .catch(() => {
        // The fallback mark keeps login usable when branding is unavailable.
      });
    return () => { mounted = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      navigate(user.role === 'admin' ? '/admin/dashboard' : '/coach/dashboard');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1117] pitch-gradient flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-[#2BB5D8]/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-[420px] relative z-10">
        {/* Logo card */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#2BB5D8] to-[#1a8fac] rounded-2xl mb-4 shadow-xl shadow-[#2BB5D8]/20 overflow-hidden">
            {logo ? (
              <img
                src={logo}
                alt="Pitch Basketball Academy logo"
                onError={() => setLogo(null)}
                className="max-h-full max-w-full object-contain p-1"
              />
            ) : (
              <span className="text-2xl font-black text-white">P</span>
            )}
          </div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">PITCH</h1>
          <p className="text-sm text-muted-foreground mt-1">Basketball Academy · Evaluation System</p>
        </div>

        {/* Login form */}
        <div className="bg-[#111827] border border-border/60 rounded-2xl p-8 shadow-2xl">
          <h2 className="text-lg font-semibold mb-6">Sign in to your account</h2>

          {error && (
            <Alert variant="destructive" className="mb-4 bg-destructive/10 border-destructive/30">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm">Email address</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="coach@pitchacademy.com"
                  value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  required
                  className="pl-9 bg-secondary/50 border-border/60"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm">Password</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid="password-input"
                  type={showPass ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  required
                  className="pl-9 pr-10 bg-secondary/50 border-border/60"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              data-testid="login-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold mt-2"
            >
              {loading ? (
                <><Loader2 size={16} className="animate-spin mr-2" /> Signing in...</>
              ) : 'Sign in'}
            </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Contact your admin to get access or reset your password.
          </p>
        </div>

        <p className="text-center text-[11px] text-muted-foreground/50 mt-6">
          © 2025 Pitch Basketball Academy · Internal Use Only
        </p>
      </div>
    </div>
  );
}
